import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Robot, Clock, CheckCircle, WhatsappLogo,
  PaperPlaneTilt, Spinner, CalendarCheck,
  ChatCircleDots, Users, Megaphone, ArrowRight,
  FileText, ChartBar, Truck, QrCode, Power, PlugsConnected,
  WifiHigh, WifiSlash, Check
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

import { BASE as API } from '../config/api';
import { fillTemplate, formatEuro, needsQuote as templateNeedsQuote, normalizePhone, waLink } from '../lib/whatsapp';

export default function AssistenteOperativoPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);

  // WAHA WhatsApp state
  const [waStatus, setWaStatus] = useState({ waha_configured: false, session_status: 'DISCONNECTED' });
  const [waDestNumber, setWaDestNumber] = useState('');
  const [waMessage, setWaMessage] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [waMessages, setWaMessages] = useState([]);
  const [waTemplateLoading, setWaTemplateLoading] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const qrPollRef = useRef(null);
  const [waTemplates, setWaTemplates] = useState([]);
  // Without WAHA on the server, messages open in the user's own WhatsApp: nothing to connect
  const linkMode = waStatus.mode !== 'waha';

  const fetchQuotes = useCallback(async () => {
    try { const res = await fetch(`${API}/api/quotes`, { credentials: 'include' }); const d = await res.json(); setQuotes(Array.isArray(d) ? d : []); } catch (err) { console.error(err); }
  }, []);
  const fetchClients = useCallback(async () => {
    try { const res = await fetch(`${API}/api/clients`, { credentials: 'include' }); const d = await res.json(); setClients(Array.isArray(d) ? d : []); } catch (err) { console.error(err); }
  }, []);
  const fetchStats = useCallback(async () => {
    try { const res = await fetch(`${API}/api/operative/stats`, { credentials: 'include' }); const d = await res.json(); setStats(d || {}); } catch (err) { console.error(err); }
  }, []);
  const fetchReminders = useCallback(async () => {
    try { const res = await fetch(`${API}/api/reminders?completed=false`, { credentials: 'include' }); const d = await res.json(); setReminders(Array.isArray(d) ? d : []); } catch (err) { console.error(err); }
  }, []);
  const fetchWaMessages = useCallback(async () => {
    try { const res = await fetch(`${API}/api/whatsapp/messages`, { credentials: 'include' }); const d = await res.json(); setWaMessages(Array.isArray(d) ? d : []); } catch (err) { console.error(err); }
  }, []);
  const fetchWaTemplates = useCallback(async () => {
    try { const res = await fetch(`${API}/api/whatsapp/templates`, { credentials: 'include' }); const d = await res.json(); setWaTemplates(Array.isArray(d) ? d : []); } catch (err) { console.error(err); }
  }, []);

  const fetchWaStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/settings`, { credentials: 'include' });
      const data = await res.json();
      setWaStatus(data || {});
      // If session is WORKING, stop QR polling
      if (data.session_status === 'WORKING' && qrPollRef.current) {
        clearInterval(qrPollRef.current);
        qrPollRef.current = null;
        setQrCode(null);
        toast.success('WhatsApp connesso!');
      }
      return data;
    } catch (err) { console.error(err); return null; }
  }, []);

  useEffect(() => {
    fetchQuotes(); fetchClients(); fetchStats(); fetchReminders(); fetchWaMessages(); fetchWaStatus(); fetchWaTemplates();
  }, [fetchQuotes, fetchClients, fetchStats, fetchReminders, fetchWaMessages, fetchWaStatus, fetchWaTemplates]);

  // Auto-fetch QR when status is SCAN_QR_CODE and we don't have a QR yet
  useEffect(() => {
    if (waStatus.session_status === 'SCAN_QR_CODE' && !qrCode && !qrLoading) {
      fetchQrCode();
    }
  }, [waStatus.session_status, qrCode, qrLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup polling on unmount
  useEffect(() => { return () => { if (qrPollRef.current) clearInterval(qrPollRef.current); }; }, []);

  // Auto-fill dest number from selected client
  useEffect(() => {
    if (selectedClientId) {
      const client = (clients || []).find(c => c.id === selectedClientId);
      if (client?.phone) setWaDestNumber(client.phone);
    }
  }, [selectedClientId, clients]);

  // Auto-fill client from selected quote
  useEffect(() => {
    if (selectedQuoteId) {
      const quote = (quotes || []).find(q => q.id === selectedQuoteId);
      if (quote?.client_id && quote.client_id !== selectedClientId) setSelectedClientId(quote.client_id);
    }
  }, [selectedQuoteId, quotes, selectedClientId]);

  const startSession = async () => {
    setSessionLoading(true);
    setQrCode(null);
    try {
      const res = await fetch(`${API}/api/whatsapp/session/start`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success(t('assistant.startSession'));
      // Poll for QR with retries (WhatsApp Web needs time to generate QR)
      let retries = 0;
      const maxRetries = 10;
      const pollQr = async () => {
        retries++;
        const qrRes = await fetch(`${API}/api/whatsapp/session/qr`, { credentials: 'include' });
        const qrData = await qrRes.json();
        if (qrData.qr) {
          setQrCode(qrData.qr);
          setSessionLoading(false);
          if (!qrPollRef.current) {
            qrPollRef.current = setInterval(async () => {
              await fetchWaStatus();
              // Also refresh QR periodically (it expires)
              try {
                const r = await fetch(`${API}/api/whatsapp/session/qr`, { credentials: 'include' });
                const d = await r.json();
                if (d.qr) setQrCode(d.qr);
              } catch (e) { /* ignore */ }
            }, 5000);
          }
        } else if (retries < maxRetries) {
          setTimeout(pollQr, 2000);
        } else {
          setSessionLoading(false);
          toast.error(t('assistant.qrNotAvailable'));
        }
      };
      setTimeout(pollQr, 3000);
    } catch (err) {
      toast.error(err.message);
      setSessionLoading(false);
    }
  };

  const fetchQrCode = async () => {
    setQrLoading(true);
    try {
      const res = await fetch(`${API}/api/whatsapp/session/qr`, { credentials: 'include' });
      const data = await res.json();
      if (data.qr) {
        setQrCode(data.qr);
        if (!qrPollRef.current) {
          qrPollRef.current = setInterval(fetchWaStatus, 5000);
        }
      } else {
        await fetchWaStatus();
      }
    } catch (err) { toast.error(t('assistant.qrError')); }
    finally { setQrLoading(false); }
  };

  const stopSession = async () => {
    try {
      await fetch(`${API}/api/whatsapp/session/stop`, { method: 'POST', credentials: 'include' });
      if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
      setQrCode(null);
      toast.success(t('assistant.sessionDisconnected'));
      fetchWaStatus();
    } catch (err) { toast.error(t('assistant.disconnectError')); }
  };

  const completeReminder = async (id) => {
    try {
      await fetch(`${API}/api/reminders/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ completed: true }) });
      fetchReminders(); fetchStats();
    } catch (err) { toast.error('Errore'); }
  };

  const sendWaMessage = async (messageText) => {
    const msg = messageText || waMessage;
    if (!msg.trim()) return;
    if (!waDestNumber.trim()) { toast.error(t('assistant.enterNumber')); return; }
    if (linkMode) {
      // Opens the user's own WhatsApp with number and text filled in (before any await: no pop-up block)
      const number = normalizePhone(waDestNumber);
      if (!number) { toast.error('Numero di telefono non valido'); return; }
      window.open(waLink(number, msg), '_blank', 'noopener');
      if (!messageText) setWaMessage('');
      try {
        await fetch(`${API}/api/whatsapp/log`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ to_number: number, message: msg, quote_id: selectedQuoteId || null, client_id: selectedClientId || null })
        });
        fetchWaMessages();
      } catch (e) { /* the message was opened anyway */ }
      return;
    }
    if (waStatus.session_status !== 'WORKING') { toast.error(t('assistant.waNotConnected')); return; }
    setWaSending(true);
    try {
      const res = await fetch(`${API}/api/whatsapp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ to_number: waDestNumber, message: msg })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Errore invio'); }
      toast.success(t('assistant.sentSuccess'));
      if (!messageText) setWaMessage('');
      fetchWaMessages();
    } catch (err) { toast.error(`${t('assistant.sendError')}: ${err.message}`); }
    finally { setWaSending(false); }
  };

  const resolveTemplate = (tmpl) => {
    const quote = selectedQuoteId ? (quotes || []).find(q => q.id === selectedQuoteId) : null;
    const client = selectedClientId ? (clients || []).find(c => c.id === selectedClientId) : null;
    return fillTemplate(tmpl.body, { quote, client, companyName: user?.company_name });
  };

  const sendTemplate = async (tmpl) => {
    const needsQuote = tmpl.body.includes('{numero_preventivo}') || tmpl.body.includes('{totale}') || tmpl.body.includes('{num_prodotti}') || tmpl.body.includes('{stato}');
    const needsClient = tmpl.body.includes('{nome_cliente}');
    if (needsQuote && !selectedQuoteId && !selectedClientId) { toast.error(t('assistant.selectQuoteOrClient')); return; }
    if (needsClient && !selectedClientId && !selectedQuoteId) { toast.error(t('assistant.selectAClient')); return; }
    setWaTemplateLoading(tmpl.id);
    await sendWaMessage(resolveTemplate(tmpl));
    setWaTemplateLoading('');
  };

  const selectedQuote = (quotes || []).find(q => q.id === selectedQuoteId);
  const isConnected = waStatus.session_status === 'WORKING';
  const isScanning = waStatus.session_status === 'SCAN_QR_CODE';

  return (
    <div className="space-y-6 max-w-6xl" data-testid="assistente-operativo">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-bold text-[#0A0F1A] flex items-center gap-2">
          <Robot size={28} weight="duotone" className="text-[#0033A0]" />
          {t('assistant.title')}
        </h1>
        <p className="text-sm text-[#4A5568] mt-1">{t('assistant.coordination')}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#0033A0]">{stats.open_tickets}</p>
            <p className="text-[10px] text-[#4A5568]">{t('assistant.openTickets')}</p>
          </CardContent></Card>
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#0033A0]">{stats.pending_reminders}</p>
            <p className="text-[10px] text-[#4A5568]">{t('assistant.activeReminders')}</p>
          </CardContent></Card>
          <Card className={`border-[#E2E8F0] ${stats.overdue_reminders > 0 ? 'bg-red-50 border-red-200' : ''}`}><CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${stats.overdue_reminders > 0 ? 'text-red-600' : 'text-[#0033A0]'}`}>{stats.overdue_reminders}</p>
            <p className="text-[10px] text-[#4A5568]">{t('assistant.overdue')}</p>
          </CardContent></Card>
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#25D366]">{(waMessages || []).length}</p>
            <p className="text-[10px] text-[#4A5568]">{t('assistant.waMessages')}</p>
          </CardContent></Card>
        </div>
      )}

      {/* WhatsApp WAHA Hub */}
      <Card className="border-[#25D366]/30 bg-[#25D366]/[0.03]" data-testid="whatsapp-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
            {t('assistant.waComms')}
            {linkMode ? (
              <Badge variant="outline" className="text-[10px] ml-2 text-[#128C4B] border-[#25D366] flex items-center gap-1">
                <WhatsappLogo size={10} weight="fill" /> Dal tuo WhatsApp
              </Badge>
            ) : isConnected ? (
              <Badge className="bg-[#25D366] text-white text-[10px] border-0 ml-2 flex items-center gap-1">
                <WifiHigh size={10} /> {t('assistant.waConnectedBadge')}
              </Badge>
            ) : waStatus.session_status === 'WAHA_UNREACHABLE' ? (
              <Badge variant="outline" className="text-[10px] ml-2 text-red-600 border-red-300 flex items-center gap-1">
                <WifiSlash size={10} /> {t('assistant.waUnreachableBadge')}
              </Badge>
            ) : isScanning ? (
              <Badge className="bg-amber-500 text-white text-[10px] border-0 ml-2 flex items-center gap-1">
                <QrCode size={10} /> {t('assistant.waScanningBadge')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] ml-2 flex items-center gap-1">
                <WifiSlash size={10} /> {t('assistant.waDisconnectedBadge')}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Connection section */}
          {!linkMode && !isConnected ? (
            <div className="space-y-3">
              {/* QR Code + Connect */}
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="flex-1 space-y-3">
                  <p className="text-sm text-[#4A5568]">
                    {t('assistant.waConnectDesc')}
                    <span className="text-xs block mt-1 text-[#718096]">{t('assistant.waConnectInstructions')}</span>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={isScanning ? fetchQrCode : startSession}
                      disabled={sessionLoading}
                      className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                      data-testid="wa-connect-btn"
                    >
                      {sessionLoading ? <Spinner size={16} className="animate-spin mr-1" /> : <PlugsConnected size={16} className="mr-1" />}
                      {isScanning ? t('assistant.refreshQr') : t('assistant.connectWhatsApp')}
                    </Button>
                    {isScanning && (
                      <Button variant="outline" size="sm" onClick={fetchQrCode} disabled={qrLoading}>
                        <QrCode size={16} className="mr-1" /> {t('assistant.reloadQr')}
                      </Button>
                    )}
                  </div>
                </div>
                {/* QR Code display */}
                {qrCode && (
                  <div className="bg-white p-4 rounded-sm border border-[#25D366]/30 shadow-sm flex flex-col items-center" data-testid="wa-qr-code">
                    <QRCodeSVG
                      value={qrCode}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="M"
                    />
                    <p className="text-[10px] text-center text-[#4A5568] mt-2">{t('assistant.scanWithWhatsApp')}</p>
                  </div>
                )}
                {qrLoading && !qrCode && (
                  <div className="w-48 h-48 bg-gray-100 rounded-sm flex items-center justify-center">
                    <Spinner size={32} className="animate-spin text-[#25D366]" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Connected - full messaging UI */
            <div className="space-y-4">
              {linkMode ? (
                <p className="text-sm text-[#4A5568]" data-testid="wa-link-mode-info">
                  Scegli il cliente o il preventivo e il messaggio: si apre il tuo WhatsApp con numero e testo già pronti, tu premi Invia.
                  Per mandare il PDF come allegato usa il pulsante WhatsApp nella pagina del preventivo.
                </p>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#25D366] font-medium flex items-center gap-1.5">
                    <WifiHigh size={16} /> {t('assistant.waConnectedMsg')}
                  </p>
                  <Button variant="ghost" size="sm" className="text-xs text-red-500 hover:bg-red-50 h-7" onClick={stopSession}>
                    <Power size={14} className="mr-1" /> {t('assistant.disconnect')}
                  </Button>
                </div>
              )}

              {/* Context selectors + dest number */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{t('assistant.quoteLabel')}</label>
                  <Select value={selectedQuoteId} onValueChange={setSelectedQuoteId}>
                    <SelectTrigger className="bg-white h-9 text-sm" data-testid="select-quote-wa">
                      <SelectValue placeholder={t('assistant.selectQuotePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(quotes || []).map(q => (
                        <SelectItem key={q.id} value={q.id}>
                          #{q.quote_number} - {q.client_name} ({formatEuro(q.total ?? q.total_amount)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{t('assistant.clientLabel')}</label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger className="bg-white h-9 text-sm" data-testid="select-client-wa">
                      <SelectValue placeholder={t('assistant.selectClientPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(clients || []).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.phone ? `(${c.phone})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#4A5568] mb-1 block">{t('assistant.destNumber')}</label>
                  <Input
                    value={waDestNumber}
                    onChange={e => setWaDestNumber(e.target.value)}
                    placeholder="+39 333 1234567"
                    className="bg-white h-9 text-sm"
                    data-testid="wa-dest-number-input"
                  />
                </div>
              </div>

              {selectedQuote && (
                <div className="bg-white p-2.5 rounded-sm border border-[#E2E8F0] text-sm">
                  <div className="flex items-center gap-4">
                    <span className="font-medium">#{selectedQuote.quote_number}</span>
                    <span className="text-[#4A5568]">{selectedQuote.client_name}</span>
                    <Badge variant="outline" className="text-[10px]">{selectedQuote.status}</Badge>
                    <span className="text-[#0033A0] font-medium ml-auto">{formatEuro(selectedQuote.total ?? selectedQuote.total_amount)}</span>
                  </div>
                </div>
              )}

              {/* Template quick actions */}
              <div>
                <p className="text-xs font-medium text-[#4A5568] mb-2">{t('assistant.quickMessages')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(waTemplates || []).map((tmpl) => {
                    const IconMap = { FileText, ChatCircleDots, CalendarCheck, Megaphone, Truck };
                    const Icon = IconMap[tmpl.icon] || ChatCircleDots;
                    const needsQuote = templateNeedsQuote(tmpl.body);
                    const needsClient = tmpl.body.includes('{nome_cliente}') && !needsQuote;
                    return (
                      <Button
                        key={tmpl.id}
                        variant="outline" size="sm"
                        className="h-auto py-2.5 px-3 text-xs border-[#25D366]/40 hover:bg-[#25D366]/10 hover:border-[#25D366] flex flex-col items-center gap-1.5"
                        onClick={() => sendTemplate(tmpl)}
                        disabled={!waDestNumber || waSending || (needsQuote && !selectedQuoteId) || (needsClient && !selectedClientId)}
                        data-testid={`wa-send-${tmpl.category}-btn`}
                      >
                        {waTemplateLoading === tmpl.id ? <Spinner size={18} className="animate-spin" /> : <Icon size={18} className="text-[#25D366]" />}
                        <span className="line-clamp-1">{tmpl.name}</span>
                      </Button>
                    );
                  })}
                  {(waTemplates || []).length === 0 && (
                    <p className="col-span-4 text-xs text-[#4A5568] text-center py-2">
                      {t('assistant.noTemplates')}
                    </p>
                  )}
                </div>
              </div>

              {/* Free message */}
              <div>
                <p className="text-xs font-medium text-[#4A5568] mb-2">{t('assistant.freeMessage')}</p>
                <div className="flex gap-2">
                  <Input
                    value={waMessage} onChange={e => setWaMessage(e.target.value)}
                    placeholder={t('assistant.typeMessage')}
                    className="flex-1"
                    onKeyDown={e => e.key === 'Enter' && sendWaMessage()}
                    data-testid="wa-message-input"
                  />
                  <Button
                    onClick={() => sendWaMessage()}
                    disabled={waSending || !waMessage.trim() || !waDestNumber}
                    className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                    data-testid="wa-send-btn"
                  >
                    <PaperPlaneTilt size={16} />
                  </Button>
                </div>
              </div>

              {/* Message history */}
              {(waMessages || []).length > 0 && (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  <p className="text-xs font-medium text-[#4A5568]">{t('assistant.messageHistory')}</p>
                  {(waMessages || []).slice(0, 8).map((m, i) => (
                    <div key={i} className="p-2 bg-white border border-[#E2E8F0] rounded-sm text-xs">
                      <div className="flex justify-between mb-0.5">
                        <span className="text-[#4A5568]">{m.to_number} - {new Date(m.created_at).toLocaleString('it-IT')}</span>
                        <Badge className={`text-[9px] ${m.status === 'sent' || m.status === 'opened' ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-700 hover:bg-gray-100'}`}>{m.status === 'opened' ? 'aperto in WhatsApp' : m.status === 'sent' ? 'inviato' : m.status}</Badge>
                      </div>
                      <p className="text-[#0A0F1A] line-clamp-2">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reminders */}
      {(reminders || []).length > 0 && (
        <Card className="border-[#E2E8F0]" data-testid="reminders-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock size={20} weight="duotone" className="text-[#0033A0]" />
              {t('assistant.activeRemindersTitle')} ({(reminders || []).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(reminders || []).map((r) => {
                const isOverdue = new Date(r.due_date) < new Date();
                return (
                  <div key={r.id} className={`flex items-center justify-between p-2.5 rounded-sm border text-sm ${isOverdue ? 'border-red-200 bg-red-50' : 'border-[#E2E8F0]'}`}>
                    <div className="flex items-center gap-3">
                      <button onClick={() => completeReminder(r.id)} className="text-gray-400 hover:text-green-600 transition-colors" data-testid={`complete-reminder-${r.id}`}>
                        <CheckCircle size={20} />
                      </button>
                      <div>
                        <p className="font-medium">{r.title}</p>
                        <p className="text-xs text-[#4A5568]">
                          {t('assistant.deadlineLabel')} {new Date(r.due_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'it-IT')}
                          {isOverdue && <span className="text-red-600 ml-2 font-medium">{t('assistant.overdueLabel')}</span>}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{r.reminder_type}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature sections */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="features-section">
        <FeatureCard icon={<Users size={20} weight="duotone" className="text-[#0033A0]" />} title={t('assistant.coordTitle')} description={t('assistant.coordDesc')} features={[t('assistant.coordFeature1'), t('assistant.coordFeature2'), t('assistant.coordFeature3')]} />
        <FeatureCard icon={<Truck size={20} weight="duotone" className="text-[#0033A0]" />} title={t('assistant.ordersTitle')} description={t('assistant.ordersDesc')} features={[t('assistant.ordersFeature1'), t('assistant.ordersFeature2'), t('assistant.ordersFeature3')]} />
        <FeatureCard icon={<ChartBar size={20} weight="duotone" className="text-[#0033A0]" />} title={t('assistant.reportsTitle')} description={t('assistant.reportsDesc')} features={[t('assistant.reportsFeature1'), t('assistant.reportsFeature2'), t('assistant.reportsFeature3')]} />
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, features }) {
  return (
    <Card className="border-[#E2E8F0] hover:border-[#0033A0]/30 transition-colors">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">{icon}{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#4A5568] mb-3">{description}</p>
        <ul className="text-xs text-[#4A5568] space-y-1">
          {features.map((f, i) => (<li key={i} className="flex items-center gap-1.5"><ArrowRight size={10} className="text-[#0033A0]" /> {f}</li>))}
        </ul>
      </CardContent>
    </Card>
  );
}

